import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TaxonomyChild } from 'app/api/tag-queries/types';
import { TagText } from './TaxonomyItemText';
import { getConceptTypeContent, getTagLabel } from 'app/utils/tag-utils';
import { useProgressiveRender } from './useProgressiveRender';
import { TagContext } from 'app/state/contexts/TagContext';

import './Taxonomy.scss';

interface TaxonomyItemProps extends TaxonomyChild {
  level: number;
  usedTags: string[];
  extensionTags: string[];
  selectedTagId: string | null;
  nodePath: string;
  selectedNodePath: string | null;
  expandToIds: Set<string> | null;
  searchExpandIds?: Set<string> | null;
  searchVisibleIds?: Set<string> | null;
  isSearching?: boolean;
  onSelectTag?: (tag: TaxonomyChild, nodePath?: string) => void;
  onDetailsClick?: (tag: TaxonomyChild) => void;
  onExtensionClick?: (tagId: string) => void;
}

export const TaxonomyItem = React.memo(
  ({
    id,
    labels,
    conceptType,
    children,
    level,
    usedTags,
    extensionTags,
    selectedTagId,
    nodePath,
    selectedNodePath,
    expandToIds,
    searchExpandIds,
    searchVisibleIds,
    isSearching,
    onSelectTag,
    onDetailsClick,
    onExtensionClick,
    isAbstract,
    order,
    preferredLabel,
  }: TaxonomyItemProps) => {
    const [isOpen, setIsOpen] = useState(expandToIds?.has(id) ?? false);
    const searchShouldOpen = searchExpandIds?.has(id) ?? false;

    const prevSearchShouldOpen = useRef(searchShouldOpen);
    useEffect(() => {
      if (searchShouldOpen && !prevSearchShouldOpen.current) setIsOpen(true);
      prevSearchShouldOpen.current = searchShouldOpen;
    }, [searchShouldOpen]);

    const effectiveIsOpen = isOpen;
    const itemRef = useRef<HTMLDivElement>(null);

    const { tagLanguage } = useContext(TagContext);
    const label = useMemo(() => getTagLabel({ languages: labels, currentLang: tagLanguage }), [labels, tagLanguage]);
    const { conceptLabel, icon } = useMemo(() => getConceptTypeContent(conceptType), [conceptType]);

    // Memoize the taxonomy object to avoid recreating on every render
    const taxonomyItem = useMemo<TaxonomyChild>(
      () => ({ id, labels, conceptType, children, isAbstract, order, preferredLabel }),
      [id, labels, conceptType, children, isAbstract, order, preferredLabel]
    );

    // TODO: Replace with actual used state from backend/context
    const isUsed = useMemo(() => usedTags.includes(id), [usedTags, id]);
    const isExtension = useMemo(() => extensionTags.includes(id), [extensionTags, id]);
    const isSelected = selectedTagId === id && (selectedNodePath === null || selectedNodePath === nodePath);

    // Scroll to selected node on tab entry only, not on user clicks within taxonomy
    const canAutoScroll = useRef(expandToIds !== null);
    useEffect(() => {
      if (canAutoScroll.current && isSelected) {
        canAutoScroll.current = false;
        const timer = setTimeout(() => {
          itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
        return () => clearTimeout(timer);
      }
      if (selectedTagId !== null) {
        canAutoScroll.current = false;
      }
    }, [isSelected, selectedTagId]);

    const handleToggle = useCallback(() => {
      setIsOpen((prev) => !prev);
      if (!isAbstract) onSelectTag?.(taxonomyItem, nodePath);
    }, [isAbstract, onSelectTag, taxonomyItem, nodePath]);

    const handleSelect = useCallback(() => {
      if (!isAbstract) onSelectTag?.(taxonomyItem, nodePath);
    }, [isAbstract, onSelectTag, taxonomyItem, nodePath]);
    const handleDetails = () => {
      onDetailsClick?.(taxonomyItem);
    };
    const handleExtension = useCallback(() => onExtensionClick?.(id), [onExtensionClick, id]);

    const filteredChildren = useMemo(
      () => (isSearching && searchVisibleIds ? children.filter((c) => searchVisibleIds.has(c.id)) : children),
      [children, isSearching, searchVisibleIds]
    );
    const visibleChildren = useProgressiveRender(filteredChildren, effectiveIsOpen);

    if (children.length > 0) {
      return (
        <li className="pos-rel">
          {effectiveIsOpen && <span className="taxonomy__dotted-line taxonomy__dotted-line--with-children" />}

          <div className="taxonomy__list-wrapper">
            <span className="taxonomy__prefix-line" />

            <div ref={itemRef} id={id} className="flex-row cursor-pointer" onClick={handleToggle}>
              <div className={`taxonomy__icon ${effectiveIsOpen ? '' : 'icon-closed'}`}>
                <i className="fa-solid fa-caret-down"></i>
              </div>

              <TagText
                label={label || ''}
                conceptLabel={conceptLabel}
                icon={icon}
                isUsed={isUsed}
                isExtension={isExtension}
                isSelected={isSelected}
                onDetailsClick={onDetailsClick && handleDetails}
              />
            </div>
          </div>

          {effectiveIsOpen && (
            <ul className="taxonomy__child-wrapper child-indent pos-rel">
              {visibleChildren.map((child, idx) => (
                <TaxonomyItem
                  key={`${child.id}|${idx}`}
                  {...child}
                  level={level + 1}
                  usedTags={usedTags}
                  extensionTags={extensionTags}
                  onSelectTag={onSelectTag}
                  selectedTagId={selectedTagId}
                  nodePath={`${nodePath}>${child.id}`}
                  selectedNodePath={selectedNodePath}
                  expandToIds={expandToIds}
                  searchExpandIds={searchExpandIds}
                  searchVisibleIds={searchVisibleIds}
                  isSearching={isSearching}
                  onDetailsClick={onDetailsClick}
                  onExtensionClick={onExtensionClick}
                />
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li className="pos-rel">
        {effectiveIsOpen && <span className="taxonomy__dotted-line taxonomy__dotted-line--leaf" />}
        <div className="taxonomy__list-wrapper">
          <span className={level > 1 ? 'taxonomy__prefix-line large' : 'taxonomy__prefix-line'} />

          <div ref={itemRef} id={id} className="flex-row" onClick={handleSelect}>
            <TagText
              label={label || ''}
              conceptLabel={conceptLabel}
              icon={icon}
              isUsed={isUsed}
              isExtension={isExtension}
              isSelected={isSelected}
              onDetailsClick={onDetailsClick && handleDetails}
              onExtensionClick={onExtensionClick && handleExtension}
            />
          </div>
        </div>
      </li>
    );
  }
);
